import { useAuthActions } from '@convex-dev/auth/react'
import { FileTextIcon, SignOutIcon, TagIcon, TrashIcon } from '@phosphor-icons/react'
import { useNavigate, Link } from 'react-router'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import * as React from 'react'

import { InlineEdit } from '@/components/InlineEdit'
import { PDFDropzone } from '@/components/PDFDropzone'
import { TagBadge } from '@/components/TagBadge'
import { TagFilterBar } from '@/components/TagFilterBar'
import { TagPicker } from '@/components/TagPicker'
import { ThemeToggle } from '@/components/ThemeToggle'
import { BarLoader } from '@repo/ui/components/bar-loader'
import { Button } from '@repo/ui/components/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@repo/ui/components/empty'
import { Spinner } from '@repo/ui/components/spinner'
import { toastManager } from '@repo/ui/components/toast'
import { computeSha256, uploadDocument } from '@/lib/api'
import { api } from '@repo/convex/api'
import type { Doc } from '@repo/convex/dataModel'

export default function Home() {
	const navigate = useNavigate()
	const { isAuthenticated, isLoading } = useConvexAuth()
	const { signOut } = useAuthActions()
	const recentDocs = useQuery(api.documents.listDocuments)
	const allTags = useQuery(api.tags.listTags)
	const initUpload = useMutation(api.documents.initUpload)
	const deleteDocument = useMutation(api.documents.deleteDocument)
	const renameDocument = useMutation(api.documents.renameDocument)
	const [editingDoc, setEditingDoc] = React.useState<Doc<'hearsay_documents'> | null>(null)

	React.useEffect(() => {
		if (!isLoading && !isAuthenticated) {
			navigate('/login')
		}
	}, [isLoading, isAuthenticated, navigate])

	const handleFile = React.useCallback(
		async (file: File) => {
			const docId = await computeSha256(file)
			const init = await initUpload({ docId, filename: file.name })
			if (!init.isDuplicate && init.uploadUrl) {
				await uploadDocument(file, init.documentId, init.uploadUrl)
			}
			navigate(`/reader/${docId}?view=split`)
		},
		[initUpload, navigate],
	)

	const onError = React.useCallback((msg: string) => {
		toastManager.add({ title: 'Error', description: msg, type: 'error' })
	}, [])

	const tagMap = React.useMemo(() => {
		const m = new Map<string, Doc<'hearsay_tags'>>()
		allTags?.forEach((t) => m.set(t._id, t))
		return m
	}, [allTags])

	if (isLoading || !isAuthenticated) {
		return (
			<main className="flex min-h-screen items-center justify-center">
				<Spinner className="text-muted-foreground size-8" />
			</main>
		)
	}

	return (
		<main className="mx-auto flex min-h-screen max-w-2xl flex-col gap-10 px-6 py-16">
			<div className="flex items-start justify-between">
				<div className="space-y-2">
					<img src="/static/images/logo.png" alt="HearSay" className="h-16 w-auto" />
					<p className="text-muted-foreground">Upload a PDF to read it aloud with sentence-by-sentence highlighting.</p>
				</div>
				<ThemeToggle />
				<Button variant="ghost" size="icon-sm" aria-label="Sign out" onClick={() => signOut()}>
					<SignOutIcon weight="thin" />
				</Button>
			</div>

			<PDFDropzone onFile={handleFile} onError={onError} />

			{recentDocs !== undefined && recentDocs.length === 0 && (
				<Empty className="py-8">
					<EmptyHeader>
						<EmptyTitle>No documents yet</EmptyTitle>
						<EmptyDescription>Upload a PDF above to get started.</EmptyDescription>
					</EmptyHeader>
				</Empty>
			)}

			{recentDocs && recentDocs.length > 0 && (
				<section className="space-y-3">
					{allTags && allTags.length > 0 && <TagFilterBar tags={allTags} />}

					<h2 className="text-muted-foreground text-sm font-medium tracking-widest uppercase">Recent</h2>

					<ul className="space-y-1">
						{recentDocs.map((doc) => (
							<li key={doc._id} className="group flex items-center gap-1">
								<Link
									className="hover:bg-muted flex flex-1 items-center gap-2 rounded-lg px-3 py-2 text-sm"
									to={`/reader/${doc.docId}?view=split`}
								>
									<FileTextIcon weight="thin" className="text-muted-foreground size-4 shrink-0" />
									<InlineEdit
										value={doc.displayName ?? doc.filename}
										onSave={(name) => renameDocument({ documentId: doc._id, displayName: name })}
										className="text-sm"
									/>
									{doc.tagIds && doc.tagIds.length > 0 && (
										<span className="flex shrink-0 items-center gap-1">
											{doc.tagIds.map((tid) => {
												const t = tagMap.get(tid)
												return t ? <TagBadge key={tid} tag={t} /> : null
											})}
										</span>
									)}
									{doc.status === 'processing' && <BarLoader className="text-muted-foreground size-3" />}
									{doc.status === 'processing' && (
										<span className="text-muted-foreground text-xs">
											{doc.retryCount && doc.retryCount > 0
												? `Retry ${doc.retryCount}`
												: doc.processingStep?.startsWith('cleaning') ? 'Cleaning'
													: doc.processingStep?.startsWith('extracting') ? 'Extracting'
													: 'Uploading'}
										</span>
									)}
								</Link>
								<Button
									variant="ghost"
									size="icon-sm"
									aria-label="Manage folders"
									className="opacity-0 group-hover:opacity-100"
									onClick={() => setEditingDoc(doc)}
								>
									<TagIcon weight="thin" className="text-muted-foreground size-4" />
								</Button>
								<Button
									variant="ghost"
									size="icon-sm"
									aria-label="Delete document"
									className="opacity-0 group-hover:opacity-100"
									onClick={() => {
										if (confirm(`Delete "${doc.filename}"?`)) {
											deleteDocument({ documentId: doc._id })
										}
									}}
								>
									<TrashIcon weight="thin" className="text-muted-foreground size-4" />
								</Button>
							</li>
						))}
					</ul>
				</section>
			)}

			{editingDoc && allTags && <TagPicker doc={editingDoc} tags={allTags} onClose={() => setEditingDoc(null)} />}
		</main>
	)
}
